import {
  type CreateUpdateOrderDTO,
  Currency,
  type IngridAddress,
  type IngridSession,
  type OrderAddressDTO,
  type OrderDTO,
  OrderStatus,
  type PostNordAddress
} from 'ecosystem'
import { convertMinorUnitToValue, isNullable } from 'shared-utils'
import { transformOrderItemsToCreateOrderItems } from '../../../../utils/orders'
import { type OrderProvisionResult } from '../types'
import { getPostNordStreetsFromAddress } from '../utils'

export const generateBasePayload = ({
  customer: _customer,
  items,
  ...oldOrder
}: OrderDTO): CreateUpdateOrderDTO => {
  return {
    ...oldOrder,
    items: items ? transformOrderItemsToCreateOrderItems(items) : [],
    status: OrderStatus.PAID,
    currency: Currency.SEK,
    shippingInfo: {
      shippingType: 'DELIVERY'
    }
  }
}

/**
 * Carry the FE-driven `defaultDetails` (delivery/pickup + addresses)
 * forward from the persisted SF order onto the provision payload for the
 * DEFAULT shipping branch (no third-party carrier configured).
 *
 * `generateBasePayload` resets `shippingInfo` to a bare
 * `{ shippingType: 'DELIVERY' }` so the payment-provider–specific
 * transformers can rebuild it from the carrier response. The DEFAULT
 * branch has no carrier — the FE already wrote the canonical shipping
 * fields via `updateDefaultShipmentHandler` (flat fields:
 * `shippingAddress`, `shippingCost`, `shippingInfo.shippingType`,
 * `shippingInfo.pickupLocationId`, …, plus
 * `shippingInfo.additionalInfo.defaultDetails`). Reapply those from
 * `oldOrder` so they survive provisioning.
 *
 * Calling this when `oldOrder.shippingInfo.additionalInfo.defaultDetails`
 * is absent is a no-op — same `basePayload` is returned.
 */
export const generatePayloadFromDefaultDetails = (
  basePayload: CreateUpdateOrderDTO,
  oldOrder: OrderDTO
): CreateUpdateOrderDTO => {
  const defaultDetails = oldOrder.shippingInfo?.additionalInfo?.defaultDetails
  if (!defaultDetails) return basePayload

  return {
    ...basePayload,
    ...(oldOrder.shippingAddress ? { shippingAddress: oldOrder.shippingAddress } : {}),
    ...(!isNullable(oldOrder.shippingCost) ? { shippingCost: oldOrder.shippingCost } : {}),
    shippingInfo: {
      ...basePayload.shippingInfo,
      ...oldOrder.shippingInfo,
      shippingType: defaultDetails.isPickupFromStore ? 'PICKUP' : 'DELIVERY'
    }
  }
}

/**
 * The freight the shopper was actually charged.
 *
 * A carrier quote — Ingrid's `pricing.price`, PostNord's `deliveryOption.price`,
 * Nshift's `widgetResult.totalPrice` — is gross at the *store's* VAT rate; the
 * carrier has no idea who the buyer is. Every checkout wrapper re-bases it onto
 * the buyer (`useBuyerShipping.rebaseShippingQuote`) before pushing it into the
 * payment session, so a zero-rated or foreign-rate buyer pays a different
 * figure than the carrier quoted. The payment provider's number — already on
 * `basePayload` from the provider-specific generator — is that figure, and it
 * has to win: otherwise the order records freight the shopper never paid, and
 * `orderAmount − shippingCost` stops equalling the goods total.
 *
 * The raw quote stays as the fallback for flows that put no freight row on the
 * payment side at all.
 */
const resolveCarrierShippingCost = (
  basePayload: CreateUpdateOrderDTO,
  carrierQuote: number | undefined
): number | undefined =>
  isNullable(basePayload.shippingCost) ? carrierQuote : basePayload.shippingCost

export const generatePayloadFromIngrid = (
  basePayload: CreateUpdateOrderDTO,
  ingridResponse?: IngridSession
) => {
  if (!ingridResponse) {
    return basePayload
  }

  const { delivery_groups } = ingridResponse
  const deliveryGroup = delivery_groups[0]
  const deliveryAddress = deliveryGroup.addresses.delivery_address
  const shipping = deliveryGroup.shipping

  const generateOrderAddressFromIngridAddress = (
    address?: IngridAddress
  ): OrderAddressDTO | undefined => {
    return address
      ? {
          address1: address.address_lines?.join(', ') || '',
          address2: '',
          country: address.country || '',
          mobilePhone: deliveryAddress.phone || '',
          postalCode: address.postal_code || '',
          postalRegion: address.city || '',
          attention: `${deliveryAddress.first_name} ${deliveryAddress.last_name}`,
          reference: `${shipping.carrier} ${shipping.delivery_type}, ${shipping.product}`
        }
      : undefined
  }

  const recipientShippingAddress = generateOrderAddressFromIngridAddress(deliveryAddress)
  const pickupAddress = generateOrderAddressFromIngridAddress(
    deliveryGroup.addresses.location?.address
  )
  const shippingAddress = pickupAddress ?? recipientShippingAddress

  const carrierName = shipping.carrier
  const carrierService = shipping.product
  const trackingNumber = deliveryGroup.tos_id
  const pickupLocationId = deliveryGroup.addresses.location?.external_id
  const pickupLocationName = deliveryGroup.addresses.location?.name

  const shippingCost = resolveCarrierShippingCost(
    basePayload,
    convertMinorUnitToValue(deliveryGroup.pricing.price)
  )

  return {
    ...basePayload,
    ...(!isNullable(shippingCost) ? { shippingCost } : {}),
    ...(shippingAddress ? { shippingAddress } : {}),
    shippingInfo: {
      ...basePayload.shippingInfo,
      shippingType:
        shipping.delivery_type === 'instore' ? ('PICKUP' as const) : ('DELIVERY' as const),
      ...(carrierName ? { carrierName } : {}),
      ...(carrierService ? { carrierService } : {}),
      ...(trackingNumber ? { trackingNumber } : {}),
      ...(pickupAddress
        ? {
            pickupAddress,
            pickupLocationName,
            pickupLocationId
          }
        : {})
    }
  }
}

export const generatePayloadFromPostNord = (
  basePayload: CreateUpdateOrderDTO,
  postnordResponse?: Required<OrderProvisionResult>['shippingProviderResult']['postnordResponse']
): CreateUpdateOrderDTO => {
  if (!postnordResponse) {
    return basePayload
  }

  if (postnordResponse.isPickupFromStore) {
    const pickupAddress: OrderAddressDTO | undefined = postnordResponse.selectedPickup && {
      address1: postnordResponse.selectedPickup?.address.address1 ?? '',
      address2: postnordResponse.selectedPickup?.address.address2 ?? '',
      country: postnordResponse.selectedPickup?.address.country ?? '',
      mobilePhone: postnordResponse.selectedPickup?.address.mobilePhone ?? '',
      postalCode: postnordResponse.selectedPickup?.address.postalCode ?? '',
      postalRegion: postnordResponse.selectedPickup?.address.postalRegion ?? '',
      attention: postnordResponse.selectedPickup?.address.attention ?? '',
      reference: postnordResponse.selectedPickup?.address.reference ?? ''
    }

    const pickupLocationName = postnordResponse.selectedPickup?.name
    const pickupLocationId = postnordResponse.selectedPickup?.id

    return {
      ...basePayload,
      ...(pickupAddress ? { shippingAddress: pickupAddress } : {}),
      shippingCost: 0,
      shippingInfo: {
        ...basePayload.shippingInfo,
        shippingType: 'PICKUP',
        additionalInfo: {
          postnordDetails: postnordResponse
        },
        ...(pickupAddress
          ? {
              pickupAddress,
              pickupLocationName,
              pickupLocationId
            }
          : {})
      }
    }
  }

  const generateOrderAddressFromPostnordAddress = (
    address?: PostNordAddress
  ): OrderAddressDTO | undefined => {
    return address
      ? {
          address1: getPostNordStreetsFromAddress(address).join(', '),
          address2: '',
          country: address.countryCode || '',
          mobilePhone: '',
          postalCode: address.postCode || '',
          postalRegion: address.city || '',
          attention: '',
          reference: `PostNord ${postnordResponse.deliveryOption?.type}`
        }
      : undefined
  }

  const recipientShippingAddress = generateOrderAddressFromPostnordAddress(
    postnordResponse.recipientAddress
  )
  const pickupAddress = generateOrderAddressFromPostnordAddress(
    postnordResponse.deliveryOption?.details.location?.address
  )
  const shippingAddress = pickupAddress ?? recipientShippingAddress

  const carrierService = postnordResponse.deliveryOption?.type
  const trackingNumber = postnordResponse.bookingDetails?.trackingNumber
  const trackingUrl = postnordResponse.bookingDetails?.trackingUrl
  const labelUrl = postnordResponse.bookingDetails?.labelUrl
  const pickupLocationId =
    postnordResponse.deliveryOption?.details.bookingInstructions.servicePointId
  const pickupLocationName = postnordResponse.deliveryOption?.details.location?.name
  const shippingCost = resolveCarrierShippingCost(
    basePayload,
    postnordResponse.deliveryOption ? (postnordResponse.deliveryOption?.price ?? 0) : undefined
  )

  return {
    ...basePayload,
    ...(shippingAddress ? { shippingAddress } : {}),
    ...(!isNullable(shippingCost) ? { shippingCost } : {}),
    shippingInfo: {
      ...basePayload.shippingInfo,
      shippingType: 'DELIVERY',
      additionalInfo: {
        postnordDetails: postnordResponse
      },
      carrierName: 'PostNord',
      ...(carrierService ? { carrierService } : {}),
      ...(trackingNumber ? { trackingNumber } : {}),
      ...(trackingUrl ? { trackingUrl } : {}),
      ...(labelUrl ? { labelUrl } : {}),
      ...(pickupAddress
        ? {
            pickupAddress,
            pickupLocationName,
            pickupLocationId
          }
        : {})
    }
  }
}

export const generatePayloadFromNshift = (
  basePayload: CreateUpdateOrderDTO,
  nshiftResponse?: Required<OrderProvisionResult>['shippingProviderResult']['nshiftResponse']
): CreateUpdateOrderDTO => {
  if (!nshiftResponse) {
    return basePayload
  }

  // todo-nshift: if needed
  if (nshiftResponse.isPickupFromStore) {
    return {
      ...basePayload,
      shippingCost: 0,
      shippingInfo: {
        ...basePayload.shippingInfo,
        shippingType: 'PICKUP',
        additionalInfo: {
          nshiftDetails: {
            isPickupFromStore: true
          }
        }
      }
    }
  }

  const recipientShippingAddress = nshiftResponse.recipientAddress
    ? {
        address1: nshiftResponse.recipientAddress.address1 ?? '',
        address2: nshiftResponse.recipientAddress.address2 ?? '',
        country: nshiftResponse.recipientAddress.country,
        mobilePhone:
          nshiftResponse.recipientAddress.mobile || nshiftResponse.recipientAddress.phone || '',
        postalCode: nshiftResponse.recipientAddress.postalCode,
        postalRegion: nshiftResponse.recipientAddress.city || '',
        attention: '',
        reference: `Nshift ${nshiftResponse.bookingDetails?.carrier || ''} ${nshiftResponse.bookingDetails?.carrierProduct || ''}`
      }
    : undefined

  const pickupAddress = nshiftResponse.bookingDetails?.pickupPoint
    ? {
        address1: nshiftResponse.bookingDetails.pickupPoint.address1 ?? '',
        address2: nshiftResponse.bookingDetails.pickupPoint.address2 ?? '',
        country: nshiftResponse.bookingDetails.pickupPoint.countryCode,
        mobilePhone:
          nshiftResponse.bookingDetails.pickupPoint.mobile ||
          nshiftResponse.bookingDetails.pickupPoint.phone ||
          '',
        postalCode: nshiftResponse.bookingDetails.pickupPoint.postalCode,
        postalRegion: nshiftResponse.bookingDetails.pickupPoint.city || '',
        attention: '',
        reference: `Nshift ${nshiftResponse.bookingDetails.carrier || ''} ${nshiftResponse.bookingDetails.carrierProduct || ''}`
      }
    : undefined

  const shippingAddress = pickupAddress ?? recipientShippingAddress

  const carrierName = nshiftResponse.bookingDetails?.carrier
  const carrierService = nshiftResponse.bookingDetails?.carrierProduct
  const pickupLocationId = nshiftResponse.bookingDetails?.pickupPoint?.id
  const pickupLocationName = nshiftResponse.bookingDetails?.pickupPoint?.name
  const shippingCost = resolveCarrierShippingCost(
    basePayload,
    nshiftResponse.widgetResult ? (nshiftResponse.widgetResult?.totalPrice ?? 0) : undefined
  )

  return {
    ...basePayload,
    ...(shippingAddress ? { shippingAddress } : {}),
    ...(!isNullable(shippingCost) ? { shippingCost } : {}),
    shippingInfo: {
      ...basePayload.shippingInfo,
      shippingType: 'DELIVERY',
      additionalInfo: {
        nshiftDetails: nshiftResponse
      },
      ...(carrierName ? { carrierName } : {}),
      ...(carrierService ? { carrierService } : {}),
      ...(pickupAddress
        ? {
            pickupAddress,
            pickupLocationName,
            pickupLocationId
          }
        : {})
    }
  }
}
